import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, AppId } from '@cld-remote/application_wrapper/index';

import { AccountDataSchema } from '../api/accountData';
import { createApiClient } from '../api/createApiClient';
import { ProductListDataSchema } from '../api/productListData';
import { Features, getFeatures } from '../utils/features';
import { ExternalLogger, UnicornApi } from '@cld/unicorn-api';
import { LoaderSpinner } from '../components/loaderSpinner';

type AppLoaderValue = [UnicornApi['data'], ExternalLogger, ProductListDataSchema, AccountDataSchema];

const AppLoaderContext = createContext<AppLoaderValue | undefined>(undefined);

interface AppDataLoaderProps extends PropsWithChildren {
  unicornApi: UnicornApi;
}

const ROLLBAR_PROGRAMMABLE_MEDIA_TOKEN = '5ad0ebd5d0fd421783eed93cb496bd5c';

// TODO: Improve or remove this context when a the "new application wrapper" will be available
export const AppDataLoader = ({ children, unicornApi }: AppDataLoaderProps) => {
  const apiClient = useMemo(() => createApiClient({ httpClient: fetch, baseClientUrl: API_BASE_URL }), [API_BASE_URL]);

  const [productList, setProductList] = useState<ProductListDataSchema | undefined>();

  useEffect(() => {
    void apiClient.fetchProductListData(unicornApi.data.productEnvironment.externalId).then((productList) => {
      setProductList(productList);
    });
  }, [unicornApi.data.productEnvironment.externalId, apiClient]);

  const programmableMediaLogger = useMemo(() => unicornApi.methods.createLogger(ROLLBAR_PROGRAMMABLE_MEDIA_TOKEN), [unicornApi.methods.createLogger]);

  if (productList === undefined) {
    return <LoaderSpinner />;
  }

  const data: AppLoaderValue = [
    unicornApi.data,
    programmableMediaLogger,
    productList,
    {
      created_at: unicornApi.data.account.__MIGRATION_createdAt,
      id: unicornApi.data.account.id,
      new_billing_system: unicornApi.data.account.newBillingSystem,
    },
  ];

  return <AppLoaderContext.Provider value={data}>{children}</AppLoaderContext.Provider>;
};

export const useAppDataLoader = () => {
  const context = useContext(AppLoaderContext);
  if (context === undefined) {
    throw new Error(`useAppLoader must be used within a AppDataLoader`);
  }
  return context;
};

interface AppDataValue {
  account: AccountDataSchema;
  unicornApiData: UnicornApi['data'];
  programmableMediaLogger: ExternalLogger;
  productList: ProductListDataSchema;
  features: Features;
}

type AppDataSuspenseProps = React.PropsWithChildren<{ fallback?: React.ReactNode; suspense?: boolean }>;

export const AppDataContext = createContext<AppDataValue | undefined>(undefined);

export const AppDataSuspense = ({ children, fallback = null, suspense = false }: AppDataSuspenseProps) => {
  const [unicornApiData, programmableMediaLogger, productList, account] = useAppDataLoader();

  const features = getFeatures({
    unicornApiData,
    productList,
    cloudinaryAssetsAppId: AppId.CloudinaryAssets,
  });
  return <AppDataContext.Provider value={{ unicornApiData, programmableMediaLogger, productList, account, features }}>{children}</AppDataContext.Provider>;
};

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (context === undefined) {
    throw new Error(`useAppData must be used within a AppDataSuspense`);
  }
  return context;
};
